import {gsap} from "gsap";
import {MathUtils} from "../../../utils/MathUtils";
import {GSAPUtils} from "../../../utils/GSAPUtils";


export class GlowFrame extends PIXI.Container {
    public frameSize:PIXI.ObservablePoint;
    public spread:PIXI.ObservablePoint;
    private _frame:PIXI.NineSlicePlane;
    private _dots:PIXI.Sprite[];
    private _container:PIXI.Container;
    private _corners:PIXI.Point[];
    private _dirty:boolean = true;
    private _sweep:PIXI.Sprite;
    private _sweepMask:PIXI.Graphics;

    public set tint(value:number) {
        this._frame.tint = value;
        this._sweep.tint = value;
        for (let dot of this._dots){
            dot.tint = value;
        }
    }


    constructor(frameSprite:PIXI.NineSlicePlane, dotSpites:PIXI.Sprite[], sweepSprite:PIXI.Sprite) {
        super();


        this._sweep = sweepSprite;
        this._sweep.blendMode = PIXI.BLEND_MODES.ADD;
        this._sweep.alpha = 0;
        this._sweepMask = new PIXI.Graphics();
        this._sweep.mask = this._sweepMask;

        this._frame = frameSprite;
        this._frame.blendMode = PIXI.BLEND_MODES.ADD;
        this._frame.alpha = 0;

        this._dots = dotSpites;
        for (let dot of this._dots){
            dot.blendMode = PIXI.BLEND_MODES.ADD;
            dot.anchor.set(0.5, 0.5);
            dot.alpha = 0;
        }


        this._corners = [];


        this._container = new PIXI.Container();
        this._container.addChild(this._sweepMask);
        this._container.addChild(this._sweep);
        this._container.addChild(this._frame);
        this._container.addChild(...this._dots);

        this.addChild(this._container);
        this.frameSize = new PIXI.ObservablePoint(this.onFrameSize, this);
        this.spread = new PIXI.ObservablePoint(this.onSpread, this);



    }

    private reCalcCorners(){
        this._corners = [
            new PIXI.Point(this.spread.x, this.spread.y),
            new PIXI.Point(this._frame.width - this.spread.x, this.spread.y),
            new PIXI.Point(this._frame.width - this.spread.x, this._frame.height - this.spread.y),
            new PIXI.Point(this.spread.x, this._frame.height - this.spread.y),
        ];

        this._sweepMask.clear();
        this._sweepMask.beginFill();
        this._sweepMask.drawRect(this.spread.x,this.spread.y,this.frameSize.x,this.frameSize.y);
        this._sweepMask.endFill();


        this.setDotRotation()
    }

    private setDotRotation(){
         this._dots[0].rotation = MathUtils.angleBetweenPoints(this._corners[0], this._corners[1]);
         this._dots[1].rotation = MathUtils.angleBetweenPoints(this._corners[1], this._corners[2]);
         this._dots[2].rotation = MathUtils.angleBetweenPoints(this._corners[2], this._corners[3]);
         this._dots[3].rotation = MathUtils.angleBetweenPoints(this._corners[3], this._corners[0]);
    }

    public onSpread() {
        this._container.pivot.set(this.spread.x, this.spread.y);
        this._dirty = true;
    }

    public onFrameSize() {
        this._frame.width = this.frameSize.x + this.spread.x * 2;
        this._frame.height = this.frameSize.y + this.spread.y * 2;
        this._dirty = true;
    }


    public show(duration:number = 0):gsap.core.Timeline{
        if (this._dirty){
            this.reCalcCorners();
        }
        const tl:gsap.core.Timeline = gsap.timeline();
        if (duration == 0){
            tl.add(gsap.set(this, {alpha:1}));
        } else {
            tl.add(gsap.to(this, {duration:duration, alpha:1, ease:"none"}));
        }
        return tl;
    }

    public hide(duration:number = 0):gsap.core.Timeline{
        const tl:gsap.core.Timeline = gsap.timeline();
        if (duration == 0){
            tl.add(gsap.to(this, {duration:0.001, alpha:0}));
        } else {
            tl.add(gsap.to(this, {duration:duration, alpha:0, ease:"none"}));
        }
        return tl;
    }

    public getWinAnimation():gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();
        tl.add(gsap.to(this._frame, {duration:0.05, alpha:1, ease:"none"}));
        tl.add(this.getEdgeDots(0.7), "<");
        tl.add(gsap.to(this._frame, {duration:0.2, alpha:0.5, ease:"none"}), ">-0.2");
        return tl;
    }

    public getLoopAnimation():gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();
        tl.add(gsap.to(this._frame, {duration:0.2, alpha:0.5, ease:"none"}));
        tl.add(this.getEdgeDots(1.0, "<0.5"), "<");
       tl.add(gsap.to(this._frame, {duration:0.2, alpha:0.5, ease:"none"}), ">-0.2");
        return tl;
    }

    public getWeldAnimation():gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();

        const edges:gsap.core.Timeline = GSAPUtils.createTimelineFromArray([
            this.getDotMoveAnimation(this._dots[1], this._corners[1], this._corners[2], false),
            this.getDotMoveAnimation(this._dots[3], this._corners[3], this._corners[0], false),
        ], 0).duration(0.8);

        tl.add(gsap.to(this._frame, {duration:0.05, alpha:1, ease:"none"}));
        tl.add(edges, "<");
        tl.add(gsap.to(this._frame, {duration:0.2, alpha:0.5, ease:"none"}), ">-0.2");
        return tl;
    }

    public getAttention(addSweep:boolean = true, animateDots:boolean = true) {
        const tl:gsap.core.Timeline = gsap.timeline();
        tl.add(gsap.to(this._frame, {duration:0.05, alpha:1, ease:"none"}));

        if (animateDots){
            const verticalEdges:gsap.core.Timeline = GSAPUtils.createTimelineFromArray([
                this.getDotMoveAnimation(this._dots[1], this._corners[2], this._corners[1], false),
                this.getDotMoveAnimation(this._dots[3], this._corners[3], this._corners[0], false),
            ], 0).duration(0.333);

            const horizontalEdges:gsap.core.Timeline = GSAPUtils.createTimelineFromArray([
                this.getDotMoveAnimation(this._dots[0], this._corners[0], this._corners[1], true),
                this.getDotMoveAnimation(this._dots[2], this._corners[3], this._corners[2], true)
            ], 0)
            horizontalEdges.add([
                this.getDotMoveAnimation(this._dots[0], this._corners[1], this._corners[0], true),
                this.getDotMoveAnimation(this._dots[2], this._corners[2], this._corners[3], true)
            ]);
            horizontalEdges.duration(0.666);

            tl.add(verticalEdges, "<");
            tl.add(horizontalEdges, "<");
        }

        if (addSweep){
            const sweep = this.getSweep();
            sweep.duration(0.666);
            tl.add(sweep, "<");
        }

        tl.add(gsap.to(this._frame, {duration:0.2, alpha:0.8, ease:"none"}), ">-0.2");
        return tl;
    }


    /**
     * Unit animation of 1 sec
     */

    private getSweep():gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();
        tl.add(()=>{
            this._sweep.alpha = 1;
            this._sweep.position.y = this.frameSize.y;
        });
        tl.add(gsap.to(this._sweep.position,{duration:1, y:-this._sweep.height}));
        tl.add(()=>{
            this._sweep.alpha = 0;
        });
        return tl;
    }

    private getDotMoveAnimation(dot:PIXI.Sprite, start:PIXI.Point, end:PIXI.Point, horizontal:boolean):gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();
        tl.add(()=>{
            dot.position.set(start.x, start.y);
           // dot.rotation = MathUtils.angleBetweenPoints(start, end);
        });


        const maxScale = horizontal ? this.frameSize.x / 50 : this.frameSize.y / 50;

        tl.add(gsap.to(dot, {x:end.x, y:end.y, duration:1, ease:"none"}));
        tl.add(gsap.to(dot, {pixi:{scaleX:maxScale}, duration:0.5, ease:"none"}), "<");
        tl.add(gsap.to(dot, {pixi:{scaleX:1}, duration:0.5, ease:"none"}), ">");

        tl.add(gsap.to(dot, {duration:0.2, alpha:1,  ease:"none"}), 0);
        tl.add(gsap.to(dot, {duration:0.2, alpha:0,  ease:"none"}),"-=0.2");

        return tl;
    }

    private getEdgeDots(duration:number = 1, edgeGsapPositions:gsap.Position = "<"):gsap.core.Timeline {
        const tl:gsap.core.Timeline = GSAPUtils.createTimelineFromArray([
            this.getDotMoveAnimation(this._dots[0], this._corners[0], this._corners[1], true),
            this.getDotMoveAnimation(this._dots[1], this._corners[1], this._corners[2], false),
            this.getDotMoveAnimation(this._dots[2], this._corners[2], this._corners[3], true),
            this.getDotMoveAnimation(this._dots[3], this._corners[3], this._corners[0], false),
        ], edgeGsapPositions);

        tl.duration(duration);
        return tl;
    }

    public reset() {
        this._frame.alpha = 0;
        for (let dot of this._dots){
            dot.alpha = 0;
            dot.scale.set(1,1);
        }
    }
}